import classnames from 'classnames';
import {
  IconButton,
  SearchInput,
  AppliedFilterIcon as appliedFilterIcon,
  ComponentsFilterIcon as filterIcon,
  SortAscIcon as sortAscIcon,
  SortDescIcon as sortDescIcon,
} from '@/shared/ui';
import DeviceFilterModal from './deviceFilterModal';
import useSearchAndFilterDeviceEvent from './useSearchAndFilterDeviceEvent';

// `type` isn't a valid <img> attribute — pre-existing bug. Using a typed
// tag-name reference (renders the same real <img>) avoids a JSX prop spread.
const ImgWithType: any = 'img';

export type SearchAndFilterDeviceEventProps = {
  keyRecoil: any;
  start?: any;
  stop?: any;
  timezone?: any;
  isScroll?: boolean;
  isLoadedData?: boolean;
  isDisabledSearchInput?: boolean;
};

/**
 * Search, sort and filter bar above the device event grid. useSearchAndFilterDeviceEvent supplies the search text, sort direction and current strip filter; the event-id search box is disabled until data loads, the sort button swaps ascending and descending icons, and the filter button is highlighted while an advanced filter is applied and opens the filter dialog seeded from that filter.
 *
 * @param props - Bar props carrying the tab key, study window, scroll state and the data-loaded and search-disabled flags.
 * @returns The rendered search input, sort and filter buttons together with the advanced filter dialog.
 */
const SearchAndFilterDeviceEvent = ({
  keyRecoil,
  start,
  stop,
  timezone,
  isScroll,
  isLoadedData,
  isDisabledSearchInput,
}: SearchAndFilterDeviceEventProps) => {
  const {
    stripFilter,
    isLoadingStrip,
    state,
    setState,
    isSortDesc,
    defaultStartDate,
    defaultEndDate,
    hasAppliedAdvancedFilter,
    onChangeSearchValue,
    onClickOpenAdvancedFilter,
    onClickSort,
    handleClickFilterButton,
  } = useSearchAndFilterDeviceEvent({ keyRecoil, start, stop, timezone });

  return (
    <div>
      <DeviceFilterModal
        header="FILTER"
        isOpen={state.isOpenAdvancedFilterModal}
        handleClickFilterButton={handleClickFilterButton}
        defaultEndDate={defaultEndDate}
        defaultStartDate={defaultStartDate}
        onClickClose={() => setState({ isOpenAdvancedFilterModal: false })}
        startDate={stripFilter.startSearchDate || defaultStartDate}
        endDate={stripFilter.stopSearchDate || defaultEndDate}
        isValid={stripFilter.isValid}
        eventType={stripFilter.eventType}
      />

      <div className={classnames('justify-end', isScroll ? '--has-box-shadow' : '')}>
        <div className="flex">
          <SearchInput
            className="device-event-search-input ml2"
            name="searchValue"
            type="number"
            placeholder="Search by Event ID"
            disabled={!isLoadedData && isDisabledSearchInput}
            value={state.searchValue}
            onChange={onChangeSearchValue}
            id="searchValueId"
          />
          <IconButton
            id="device-event-sort-btn"
            className="ml16px"
            isOutline
            iconComponent={
              // pre-existing bug: `type` is not a valid <img> attribute
              <ImgWithType
                src={isSortDesc ? sortDescIcon : sortAscIcon}
                alt="Sort icon"
                type={isSortDesc ? 'desc' : 'asc'}
              />
            }
            disabled={isLoadingStrip}
            onClick={onClickSort}
          />
          <IconButton
            id="device-event-filter-btn"
            className={classnames(
              'filter-icon-btn ml16px',
              hasAppliedAdvancedFilter ? '--is-applied' : '',
            )}
            isOutline
            iconComponent={
              <img
                src={hasAppliedAdvancedFilter ? appliedFilterIcon : filterIcon}
                alt="Filter icon"
              />
            }
            disabled={isLoadingStrip}
            onClick={onClickOpenAdvancedFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilterDeviceEvent;
